import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { AskAiComponent } from '../ask-ai/ask-ai.component';
import { DOMAIN } from '../constants/app.constants';
import { KBService } from '../services/kb.service';
import { HelpCenterContextService } from '../services/help-center-context.service';
import { SupportService } from '../services/support.service';
import { HelpCenterAiState, HelpCenterSectionGroup, HelpCenterView } from './help-center.types';

@Component({
  selector: 'app-help-center-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzButtonModule,
    NzCollapseModule,
    NzEmptyModule,
    NzFormModule,
    NzIconModule,
    NzInputModule,
    NzSelectModule,
    AskAiComponent
  ],
  templateUrl: './help-center-drawer.component.html',
  styleUrl: './help-center-drawer.component.less'
})
export class HelpCenterDrawerComponent implements OnInit, OnDestroy {
  @Output() productTourRequested = new EventEmitter<void>();
  currentView: HelpCenterView = 'home';
  groupedSections: HelpCenterSectionGroup[] = [];
  loadingBrowse = false;
  searchQuery = '';
  searchResults: any[] = [];
  searching = false;
  searchTouched = false;
  submittingSupport = false;
  submittingBug = false;
  submittingFeature = false;
  latestAiState: HelpCenterAiState | null = null;
  pendingAiEscalation: HelpCenterAiState | null = null;
  private readonly demoBookingUrl = 'https://outlook.office.com/book/ScanafyDemo@processoria.de/';

  readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  supportForm;
  bugForm;
  featureForm;

  constructor(
    private readonly kbService: KBService,
    private readonly supportService: SupportService,
    private readonly contextService: HelpCenterContextService,
    private readonly message: NzMessageService,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder
  ) {
    this.supportForm = this.fb.group({
      subject: ['', [Validators.required, Validators.maxLength(200)]],
      message: ['', [Validators.required, Validators.maxLength(10000)]]
    });

    this.bugForm = this.fb.group({
      subject: ['', [Validators.required, Validators.maxLength(200)]],
      whatHappened: ['', [Validators.required, Validators.maxLength(5000)]],
      expectedBehavior: ['', [Validators.required, Validators.maxLength(5000)]],
      stepsToReproduce: ['', [Validators.required, Validators.maxLength(10000)]],
      severity: ['medium', [Validators.required]]
    });

    this.featureForm = this.fb.group({
      subject: ['', [Validators.required, Validators.maxLength(200)]],
      tryingToDo: ['', [Validators.required, Validators.maxLength(5000)]],
      missingFeature: ['', [Validators.required, Validators.maxLength(5000)]],
      impact: ['', [Validators.required, Validators.maxLength(5000)]],
      workaround: ['', [Validators.maxLength(5000)]]
    });
  }

  ngOnInit(): void {
    this.loadGroupedArticles();
    this.search$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((query) => {
          const normalized = query.trim();
          if (!normalized) {
            this.searching = false;
            return of([]);
          }

          this.searching = true;
          return this.kbService.searchArticles(normalized).pipe(
            catchError(() => {
              this.message.error(this.translate.instant('HELP.SEARCH.ERROR'));
              return of([]);
            }),
            finalize(() => {
              this.searching = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.searchResults = results;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigate(view: HelpCenterView): void {
    if (view !== 'contact-support' && view !== 'report-bug' && view !== 'feature-request') {
      this.clearRequestState();
    }

    this.currentView = view;
    if (view === 'search' && this.searchQuery.trim()) {
      this.searchTouched = true;
      this.search$.next(this.searchQuery);
    }
  }

  goHome(): void {
    this.clearRequestState();
    this.currentView = 'home';
  }

  openSearchFromHome(): void {
    this.currentView = 'search';
    this.searchTouched = !!this.searchQuery.trim();
    if (this.searchTouched) {
      this.search$.next(this.searchQuery);
    }
  }

  onSearchChange(value: string): void {
    this.searchQuery = value;
    this.searchTouched = true;
    this.search$.next(value);
  }

  openArticle(slug: string): void {
    window.open(`${DOMAIN}/docs/${slug}`, '_blank', 'noopener');
  }

  openDemoBooking(event?: MouseEvent): void {
    const button = event?.currentTarget as HTMLButtonElement | null;
    window.open(this.demoBookingUrl, '_blank', 'noopener,noreferrer');
    button?.blur();
  }

  handleAiStateChange(state: HelpCenterAiState | null): void {
    this.latestAiState = state;
  }

  handleAiEscalation(type: 'support' | 'bug'): void {
    this.pendingAiEscalation = this.latestAiState ? { ...this.latestAiState } : null;
    this.currentView = type === 'bug' ? 'report-bug' : 'contact-support';
    const question = this.pendingAiEscalation?.question;
    if (question) {
      const control = type === 'bug' ? this.bugForm.get('subject') : this.supportForm.get('subject');
      if (control && !control.value) {
        control.setValue(question.slice(0, 200));
      }
    }
  }

  private loadGroupedArticles(): void {
    this.loadingBrowse = true;
    this.kbService.getGroupedArticles()
      .pipe(finalize(() => (this.loadingBrowse = false)))
      .subscribe({
        next: (res) => {
          this.groupedSections = Object.entries(res || {}).map(([sectionKey, subsectionObj]) => ({
            key: sectionKey,
            subsections: Object.entries(subsectionObj as Record<string, { title: string; slug: string }[]>)
              .map(([subsectionKey, articles]) => ({
                key: subsectionKey,
                articles
              }))
              .sort((a, b) => {
                if (!a.key) return 1;
                if (!b.key) return -1;
                return 0;
              })
          }));
        },
        error: () => {
          this.message.error(this.translate.instant('HELP.BROWSE.ERROR'));
        }
      });
  }

  submitSupport(): void {
    if (this.supportForm.invalid) {
      this.supportForm.markAllAsTouched();
      return;
    }

    this.submittingSupport = true;
    this.supportService.createSupportRequest({
      type: 'support',
      subject: this.supportForm.value.subject,
      message: this.supportForm.value.message,
      context: this.contextService.getContext(),
      aiContext: this.pendingAiEscalation || undefined
    })
      .pipe(finalize(() => (this.submittingSupport = false)))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('HELP.FORMS.SUCCESS_SUPPORT'));
          this.supportForm.reset();
          this.clearRequestState();
          this.currentView = 'home';
        },
        error: (error) => {
          this.message.error(error?.message || this.translate.instant('HELP.FORMS.ERROR_GENERIC'));
        }
      });
  }

  submitBug(): void {
    if (this.bugForm.invalid) {
      this.bugForm.markAllAsTouched();
      return;
    }

    this.submittingBug = true;
    this.supportService.createSupportRequest({
      type: 'bug',
      subject: this.bugForm.value.subject,
      bugDetails: {
        whatHappened: this.bugForm.value.whatHappened,
        expectedBehavior: this.bugForm.value.expectedBehavior,
        stepsToReproduce: this.getSteps(this.bugForm.value.stepsToReproduce),
        severity: this.bugForm.value.severity
      },
      priority: this.bugForm.value.severity,
      context: this.contextService.getContext(),
      aiContext: this.pendingAiEscalation || undefined
    })
      .pipe(finalize(() => (this.submittingBug = false)))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('HELP.FORMS.SUCCESS_BUG'));
          this.bugForm.reset({ severity: 'medium' });
          this.clearRequestState();
          this.currentView = 'home';
        },
        error: (error) => {
          this.message.error(error?.message || this.translate.instant('HELP.FORMS.ERROR_GENERIC'));
        }
      });
  }

  submitFeature(): void {
    if (this.featureForm.invalid) {
      this.featureForm.markAllAsTouched();
      return;
    }

    this.submittingFeature = true;
    this.supportService.createSupportRequest({
      type: 'feature',
      subject: this.featureForm.value.subject,
      featureDetails: {
        tryingToDo: this.featureForm.value.tryingToDo,
        missingFeature: this.featureForm.value.missingFeature,
        impact: this.featureForm.value.impact,
        workaround: this.featureForm.value.workaround
      },
      context: this.contextService.getContext(),
      aiContext: this.pendingAiEscalation || undefined
    })
      .pipe(finalize(() => (this.submittingFeature = false)))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('HELP.FORMS.SUCCESS_FEATURE'));
          this.featureForm.reset();
          this.clearRequestState();
          this.currentView = 'home';
        },
        error: (error) => {
          this.message.error(error?.message || this.translate.instant('HELP.FORMS.ERROR_GENERIC'));
        }
      });
  }

  private getSteps(value: string | null | undefined): string[] {
    return (value || '')
      .split('\n')
      .map((step) => step.trim())
      .filter(Boolean);
  }

  private clearRequestState(): void {
    this.pendingAiEscalation = null;
    this.supportForm.reset();
    this.bugForm.reset({ severity: 'medium' });
    this.featureForm.reset();
  }
}
